import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import IconButton from '../../IconButton';
import PopupMenu from '../../PopupMenu';
import CheckIcon from '../../../../public/assets/images/theme-2/icons/handin-hover.svg';
import {
  PreviewSection,
  SubmitPanel,
  Glossary,
  GlossaryHeader,
  GlossaryBody,
  GlossaryRow,
  GlossaryConcept,
  GlossaryDescription,
  GlossarySelector,
  QuizInfo,
  ResultInfo,
} from '../styles';
import { PreviewText } from '../../NodeActivityEditor/styles';

const CorrelationContentPreview = ({ data, previewText }) => {
  const [concepts, setConcepts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [redoable, setRedoable] = useState(false);
  const [done, setDone] = useState(false);
  const [menuToggle, setMenuToggle] = useState(-1);
  const [menuPopup, setMenuPopup] = useState();
  const [selectedTerms, setSelectedTerms] = useState({});
  const [corrected, setCorrected] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i >= 0; i--) {
      const j = Math.floor(Math.random() * array.length);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const resetMenuToggle = () => {
    setMenuToggle(-1);
  };

  useEffect(() => {
    const selectedValues = Object.values(selectedTerms).filter((termId) => !!termId);
    if (selectedValues.length === concepts.length) {
      setDone(true);
    } else {
      setDone(false);
    }
  }, [selectedTerms, concepts]);

  useEffect(() => {
    const newConcepts = data?.CorrelationList?.Concepts || [];
    setConcepts(newConcepts);
    const newDescriptions = newConcepts.map((concept) => ({
      id: concept.Description.id,
      body: concept.Description.body,
    }));
    setDescriptions(shuffleArray(newDescriptions));
    setRedoable(data.redoable);
    setSelectedTerms(newConcepts.reduce((acc, curr) => ((acc[curr.id] = undefined), acc), {}));
  }, []);

  useEffect(() => {
    setMenuPopup(getMenuPopup());
  }, [menuToggle]);

  const selectOption = (termId) => {
    const key = Object.keys(selectedTerms).find((key) => selectedTerms[key] === termId);
    if (key) {
      delete selectedTerms[key];
    }
    setSelectedTerms({ ...selectedTerms, [menuToggle]: termId });
    resetMenuToggle();
  };

  const getMenuOptions = () => {
    return (descriptions || []).map((description) => ({
      tag: description.id,
      name: description.body,
      selected: Object.values(selectedTerms).includes(description.id),
      onSelect: (termId) => selectOption(termId),
    }));
  };

  const getMenuPopup = () => {
    return (
      <PopupMenu
        id={`conceptPopup_${menuToggle}`}
        popupIsOpen={menuToggle !== -1}
        onClose={resetMenuToggle}
        options={getMenuOptions()}
        lightMode={true}
        alignment="left"
        border={true}
      />
    );
  };

  const showMenu = (id) => {
    if (id === menuToggle) {
      resetMenuToggle();
    } else {
      setTimeout(() => setMenuToggle(id), 10);
    }
  };

  const correctQuiz = () => {
    let correct = 0;
    concepts.forEach((concept) => {
      if (selectedTerms[concept.id] === concept.Description.id) {
        correct++;
      }
    });
    setCorrectAnswers(correct);
    setCorrected(true);
  };

  const resetQuiz = () => {
    if (redoable) {
      setSelectedTerms(concepts.reduce((acc, curr) => ((acc[curr.id] = undefined), acc), {}));
      setCorrected(false);
    }
  };

  return (
    <>
      <PreviewSection>
        <Glossary>
          <GlossaryHeader>
            <GlossaryConcept>{i18n.__('correlation_term')}</GlossaryConcept>
            <GlossaryDescription>{i18n.__('correlation_description')}</GlossaryDescription>
          </GlossaryHeader>
          <GlossaryBody>
            {concepts.map((concept, index) => {
              const { id, body, Description } = concept;
              const term = descriptions.find((term) => term.id === selectedTerms[id]);
              const className = selectedTerms[id] === Description.id ? 'correct' : 'error';
              return (
                <GlossaryRow key={id} even={index % 2}>
                  <GlossaryConcept dangerouslySetInnerHTML={{ __html: body }} />
                  <GlossarySelector
                    title={i18n.__('correlation_no_desc_chosen')}
                    onClick={() => showMenu(id)}
                    id={`conceptPopup_${id}`}
                    disabled={corrected}
                    className={corrected ? className : ''}
                    dangerouslySetInnerHTML={{ __html: term ? term.body : i18n.__('correlation_no_desc_chosen') }}
                  />
                </GlossaryRow>
              );
            })}
            {menuPopup}
          </GlossaryBody>
        </Glossary>
        {corrected && <ResultInfo>{i18n.__('quiz_correcting_result', correctAnswers, concepts.length)}</ResultInfo>}
        <QuizInfo>{i18n.__(data.redoable ? 'correlation_redoable_desc' : 'correlation_not_redoable_desc')}</QuizInfo>
        {previewText && <PreviewText dangerouslySetInnerHTML={{ __html: previewText }} />}
      </PreviewSection>
      <SubmitPanel className={'ir-hide'}>
        <IconButton
          icon={CheckIcon}
          onClick={corrected ? resetQuiz : correctQuiz}
          disabled={!done || (corrected && !redoable) || concepts.length === 0}
        >
          {corrected ? i18n.__('app_reset') : i18n.__('app_correct')}
        </IconButton>
      </SubmitPanel>
    </>
  );
};

export default CorrelationContentPreview;

CorrelationContentPreview.propTypes = {
  data: PropTypes.object.isRequired,
  previewText: PropTypes.string.isRequired,
};
